/**
 * Inventory coverage: addressable → covered → pending.
 *
 * Two framing decisions carried from the mockup, both load-bearing:
 *
 * 1. **"Pending" means stale beyond cadence**, not "exists upstream but we do
 *    not hold it". The latter has no knowable denominator; claiming it would be
 *    a number we cannot defend.
 * 2. **The out-of-scope figure is shown next to the channel percentage.** On its
 *    own, "channels 99.5% covered" reads as solved — when in fact the ≥30 k set
 *    was the only set the incumbent ever worked, and 23.5 M channels below that
 *    floor had no cadence at all. Showing coverage without the denominator it
 *    excludes is the misleading version.
 *
 * Every figure carries its snapshot age, because it IS a snapshot — the
 * underlying video question is a 20-minute scan.
 */
import { useCoverage } from '../api/queries'
import { StackedBar } from '../components/charts'
import { PanelState, Pill, Tile } from '../components/ui'
import { age, n, pct } from '../components/format'

type Buckets = Record<string, number>

const TITLES: Record<string, { title: string; hint: string }> = {
  videos_90d: { title: 'Videos · last 3 months', hint: 'yt_data · publishedat ≥ now−90d' },
  channels_30k: { title: 'Channels · ≥ 30,000 subscribers', hint: "the incumbent's tier floor" },
}

export function Coverage() {
  const { data, isLoading, error } = useCoverage()
  const metrics = data?.metrics ?? {}

  return (
    <>
      {(['videos_90d', 'channels_30k'] as const).map((key) => {
        const m = metrics[key]
        const meta = TITLES[key]!
        const b: Buckets = m?.buckets ?? {}
        const addressable = b['addressable'] ?? 0
        const fresh7 = b['fresh_7d'] ?? 0
        const fresh30 = b['fresh_30d'] ?? 0
        const stale = b['stale_30d'] ?? 0
        const mid = Math.max(0, fresh30 - fresh7)
        const covPct = addressable ? (fresh30 / addressable) * 100 : 0

        return (
          <Tile key={key} title={meta.title} hint={meta.hint}>
            <PanelState isLoading={isLoading} error={error}>
              {!m ? <p className="panel-load">no snapshot yet — the coverage role has not run</p> : (
                <>
                  <div className="cov-head">
                    <div className={`cov-pct tnum mono ${covPct >= 95 ? 'ok' : 'warn'}`}>
                      {covPct.toFixed(1)}<i>%</i>
                    </div>
                    <div className="cov-pct-l">covered<br />within 30d</div>
                    <span style={{ marginLeft: 'auto' }}>
                      {m.stale
                        ? <Pill state="warn">snapshot {age(m.age_seconds)} old</Pill>
                        : <Pill state="idle">as of {age(m.age_seconds)} ago</Pill>}
                    </span>
                  </div>
                  <StackedBar
                    label={`${meta.title}: ${pct(covPct)} covered within 30 days`}
                    segments={[
                      { name: '≤7d', value: fresh7, state: 'ok' },
                      { name: '8–30d', value: mid, state: 'flow' },
                      { name: '>30d stale', value: stale, state: 'warn' },
                    ]}
                  />
                  <table className="t" style={{ marginTop: 14 }}>
                    <tbody>
                      <tr><td>addressable</td><td className="n">{n(addressable)}</td><td className="n q">100%</td></tr>
                      <tr><td>covered — ≤ 30d</td><td className="n" style={{ color: 'var(--ok)' }}>{n(fresh30)}</td><td className="n q">{pct(covPct)}</td></tr>
                      <tr>
                        <td><b style={{ color: 'var(--warning)' }}>pending — needs a re-fetch</b></td>
                        <td className="n" style={{ color: 'var(--warning)' }}>{n(stale)}</td>
                        <td className="n q">{pct(addressable ? (stale / addressable) * 100 : 0)}</td>
                      </tr>
                      <tr><td className="q">never fetched</td><td className="n q">{n(b['never'] ?? 0)}</td><td /></tr>
                      {b['excluded'] !== undefined ? (
                        <tr><td className="q">blocked / deleted (excluded)</td><td className="n q">{n(b['excluded'])}</td><td /></tr>
                      ) : null}
                    </tbody>
                  </table>
                </>
              )}
            </PanelState>
          </Tile>
        )
      })}

      {/* The denominator the channel percentage excludes. Without this the 99.5%
          above reads as "channel coverage is solved". */}
      <Tile title="Out of scope under the old floor" hint="what the tier expansion adds">
        <PanelState isLoading={isLoading} error={error}>
          {(() => {
            const all = metrics['channels_all']?.buckets ?? {}
            const total = all['addressable'] ?? 0
            const below = all['below_floor'] ?? 0
            const above = Math.max(0, total - below)
            // Read the ≥30k coverage from the metric that measures it, rather
            // than computing something from the denominator. The first version
            // rendered `pct((above ? 1 : 0) * 100)` — always "100%", derived
            // from nothing, stated as fact.
            const inScope = metrics['channels_30k']?.buckets ?? {}
            const inScopeAddressable = inScope['addressable'] ?? 0
            const inScopeFresh = inScope['fresh_30d'] ?? 0
            const inScopePct = inScopeAddressable
              ? (inScopeFresh / inScopeAddressable) * 100
              : null
            if (!total) return <p className="panel-load">no snapshot yet</p>
            return (
              <>
                <StackedBar
                  label={`${n(below)} of ${n(total)} channels were below the 30,000-subscriber floor`}
                  segments={[
                    { name: '≥30k — worked', value: above, state: 'ok' },
                    { name: '<30k — never in scope', value: below, state: 'crit' },
                  ]}
                />
                <p className="note">
                  {inScopePct === null
                    ? 'No ≥30 k snapshot yet, so in-scope coverage is unknown.'
                    : `${pct(inScopePct)} of the ≥30 k set was really refreshed in the last 30 days.`}{' '}
                  The gap is a universe {above ? `${(below / above).toFixed(1)}×` : '—'} larger that
                  had no refresh cadence at all.
                </p>
              </>
            )
          })()}
        </PanelState>
      </Tile>
    </>
  )
}
